import React from 'react';
import { HelpCircle } from 'lucide-react';
import type { PartWithCategory } from '@/features/warranties/types/parts-by-vehicle.types';

interface CategorySectionProps {
  categoryKey: string;
  categoryLabel: string;
  parts: PartWithCategory[];
  vehicleType: 'classic' | 'collection' | 'two_wheeler';
  selectedPartIds: number[];
  isExpanded: boolean;
  canModifyDefaultParts: boolean;
  onCategoryToggle: () => void;
  onPartToggle: (partId: number, priority: number) => void;
  onSelectAllPriority: (categoryKey: string, priority: number) => void;
}

export const CategorySection: React.FC<CategorySectionProps> = ({
  categoryKey,
  categoryLabel,
  parts,
  vehicleType,
  selectedPartIds,
  isExpanded,
  canModifyDefaultParts: _canModifyDefaultParts,
  onCategoryToggle,
  onPartToggle,
  onSelectAllPriority,
}) => {
  const categoryId = `${vehicleType}_${categoryKey}`;

  // Grouper par priorité
  const partsByPriority = {
    1: parts.filter(p => p.selectionPriority === 1),
    2: parts.filter(p => p.selectionPriority === 2),
    3: parts.filter(p => p.selectionPriority === 3),
  };

  const selectedCount = parts.filter(p => selectedPartIds.includes(p.id)).length;
  const totalCount = parts.length;
  const isCategoryChecked = selectedCount > 0;

  const handleCategoryCheckboxChange = () => {
    onCategoryToggle();
  };

  const handlePartChange = (partId: number, priority: number) => {
    // Déléguer la vérification au parent qui gère les notifications
    onPartToggle(partId, priority);
  };

  const getPriorityButtonText = (priority: number): string => {
    const priorityParts = partsByPriority[priority as 1 | 2 | 3];
    const selectedInPriority = priorityParts.filter(p => selectedPartIds.includes(p.id)).length;
    return selectedInPriority === priorityParts.length ? 'aucune' : 'toutes';
  };

  return (
    <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-mb-2">
      {/* En-tête de catégorie */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border-b tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            type="checkbox"
            className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
            id={`category_${categoryId}`}
            checked={isCategoryChecked}
            onChange={handleCategoryCheckboxChange}
          />
          <label
            htmlFor={`category_${categoryId}`}
            className="text-first-letter-caps tw-text-sm tw-font-medium tw-text-gray-900 tw-uppercase tw-cursor-pointer"
          >
            {categoryLabel}
          </label>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-text-gray-500">Pièces sélectionnées</span>
          <span
            className={`tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${
              selectedCount === 0
                ? 'tw-bg-gray-200 tw-text-gray-600'
                : 'tw-bg-affinicar-primary tw-text-white'
            }`}
          >
            {selectedCount}/{totalCount}
          </span>
        </div>
      </div>

      {/* Liste des pièces (cachée si catégorie non cochée) */}
      {isExpanded && (
        <div className="tw-p-4 tw-space-y-4">
          {/* Pièces par défaut (Priorité 1) */}
          {partsByPriority[1].length > 0 && (
            <div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase">
                  Pièces par défaut
                </span>
                <div className="tw-group tw-relative">
                  <HelpCircle className="tw-w-4 tw-h-4 tw-text-gray-400 tw-cursor-help" />
                  <div className="tw-hidden group-hover:tw-block tw-absolute tw-z-10 tw-w-64 tw-p-2 tw-text-xs tw-text-white tw-bg-gray-800 tw-rounded tw-shadow-lg tw-left-0 tw-top-6">
                    Ces pièces sont indispensables pour garantir un minimum de cohérence à la catégorie. Vous pouvez toutefois en discuter avec votre commercial si vous ne souhaitez absolument pas inclure l'une de ces pièces.
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                {partsByPriority[1].map(part => (
                  <div key={part.id} className="tw-flex tw-items-start tw-gap-2">
                    <input
                      type="checkbox"
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer tw-mt-0.5"
                      id={`part_${categoryId}_${part.id}_1`}
                      checked={selectedPartIds.includes(part.id)}
                      onChange={() => handlePartChange(part.id, 1)}
                      title="Pièce par défaut - recommandée"
                    />
                    <label
                      className="text-first-letter-caps tw-text-xs tw-text-gray-700 tw-cursor-pointer tw-leading-tight"
                      htmlFor={`part_${categoryId}_${part.id}_1`}
                    >
                      {part.partLabel}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pièces fréquemment sélectionnées (Priorité 2) */}
          {partsByPriority[2].length > 0 && (
            <div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase">
                  Pièces fréquemment sélectionnées
                </span>
                <div className="tw-group tw-relative">
                  <HelpCircle className="tw-w-4 tw-h-4 tw-text-gray-400 tw-cursor-help" />
                  <div className="tw-hidden group-hover:tw-block tw-absolute tw-z-10 tw-w-64 tw-p-2 tw-text-xs tw-text-white tw-bg-gray-800 tw-rounded tw-shadow-lg tw-left-0 tw-top-6">
                    Ces pièces sont couramment incluses dans les garanties/prestations choisies par nos clients. Leur sélection dépendra néanmoins de la stratégie de service et de prix que vous souhaitez adopter.
                  </div>
                </div>
                <button
                  type="button"
                  className="tw-text-xs tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-underline"
                  onClick={() => onSelectAllPriority(categoryKey, 2)}
                >
                  {getPriorityButtonText(2)}
                </button>
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                {partsByPriority[2].map(part => (
                  <div key={part.id} className="tw-flex tw-items-start tw-gap-2">
                    <input
                      type="checkbox"
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer tw-mt-0.5"
                      id={`part_${categoryId}_${part.id}_2`}
                      checked={selectedPartIds.includes(part.id)}
                      onChange={() => handlePartChange(part.id, 2)}
                      title="Pièce fréquemment sélectionnée"
                    />
                    <label
                      className="text-first-letter-caps tw-text-xs tw-text-gray-700 tw-cursor-pointer tw-leading-tight"
                      htmlFor={`part_${categoryId}_${part.id}_2`}
                    >
                      {part.partLabel}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pièces optionnelles (Priorité 3) */}
          {partsByPriority[3].length > 0 && (
            <div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase">
                  Pièces optionnelles
                </span>
                <div className="tw-group tw-relative">
                  <HelpCircle className="tw-w-4 tw-h-4 tw-text-gray-400 tw-cursor-help" />
                  <div className="tw-hidden group-hover:tw-block tw-absolute tw-z-10 tw-w-64 tw-p-2 tw-text-xs tw-text-white tw-bg-gray-800 tw-rounded tw-shadow-lg tw-left-0 tw-top-6">
                    Ces pièces sont rarement sélectionnées. Elles le sont principalement dans le cadre de garanties/prestations dites « complètes », généralement vendues à un prix élevé au client final.
                  </div>
                </div>
                <button
                  type="button"
                  className="tw-text-xs tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-underline"
                  onClick={() => onSelectAllPriority(categoryKey, 3)}
                >
                  {getPriorityButtonText(3)}
                </button>
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                {partsByPriority[3].map(part => (
                  <div key={part.id} className="tw-flex tw-items-start tw-gap-2">
                    <input
                      type="checkbox"
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer tw-mt-0.5"
                      id={`part_${categoryId}_${part.id}_3`}
                      checked={selectedPartIds.includes(part.id)}
                      onChange={() => handlePartChange(part.id, 3)}
                      title="Pièce optionnelle"
                    />
                    <label
                      className="text-first-letter-caps tw-text-xs tw-text-gray-700 tw-cursor-pointer tw-leading-tight"
                      htmlFor={`part_${categoryId}_${part.id}_3`}
                    >
                      {part.partLabel}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
